/**
 * 器材几何注册表（engine 层，学科/实验无关）：每种器材类型一份内联 SVG 几何图元。
 * 镜像 chrome-chemistry 的 equipment-registry 模式（任务 051 决策 Q2 的同一策略）：
 * geometry 只是画法，局部坐标系原点固定在器材包围盒左上角，供 svg-renderer 按
 * `LabObject.pose` 平移。将来换成美术素材时只改这里，Scene/引擎接口零改动。
 *
 * `Reactive<T>` 字段按 `LabObject.state` 动态求值，用于表达"材质换了颜色变"
 * "砝码堆叠层数变了位置变"这类由内容包驱动、但画法本身通用的效果。
 *
 * ## 新增/修改器材模板的四点规范（todo/063 P1-07，对齐 001-方案.md §4.1「拟物实验室风」）
 * 1. **拟物质感**：真实比例 + 纹理/渐变/高光，禁止纯色块矩形——渐变走 `fill: "url(#pe-grad-*)"`
 *    引用 `svg-renderer.ts` 的 `buildDefs()` 共享定义，不要在这里各写一份 `<linearGradient>`。
 * 2. **识别标注**：中文名 + 关键部位标注（`kind: "text"`），别指望用户光看色块就认得出器材。
 * 3. **交互热点契约不变**：拖拽/交互命中点必须显式声明 `handle: "xxx"` 字段（由
 *    `svg-renderer.ts` 的 `buildGroup()` 转成 `data-handle` 属性），不要靠"数组第几个元素"
 *    这种写死下标的隐式约定——那样任何后续插入新图形都可能让热点标记错位。
 * 4. **连接锚点函数化**：挂绳/接线端点必须是可复用的导出函数（如 `blockHookLocal`/
 *    `scaleHandleLocal`），不能在这里和 `scene-manager.ts` 里各写一份坐标各自漂移。
 */

export type Reactive<T> = T | ((state: Record<string, unknown>) => T);

export function resolveReactive<T>(value: Reactive<T>, state: Record<string, unknown>): T {
  return typeof value === "function" ? (value as (state: Record<string, unknown>) => T)(state) : value;
}

interface ShapeBase {
  fill?: Reactive<string>;
  stroke?: Reactive<string>;
  strokeWidth?: Reactive<number>;
  opacity?: Reactive<number>;
  visible?: Reactive<boolean>;
  /** 旋转角度（度），围绕该图形自身局部坐标系的原点(0,0)——用于表针一类的整体旋转，
   * 替代"手算三角函数端点坐标"的旧写法（该写法在真机反馈里被指出指针视觉抖动感强）。 */
  rotate?: Reactive<number>;
  /** 声明这个图形是哪个交互热点，svg-renderer 据此打 `data-handle` 属性。用声明式字段替代
   * 之前"写死数组下标（index===2）"的脆弱约定——旧约定要求新增图形只能追加在数组末尾，
   * 不能插到中间，稍不注意就会让热点标记错位，且完全不可复用到其他器材。 */
  handle?: string;
}

export interface RectShape extends ShapeBase {
  kind: "rect";
  x: Reactive<number>;
  y: Reactive<number>;
  width: Reactive<number>;
  height: Reactive<number>;
  rx?: number;
}

export interface CircleShape extends ShapeBase {
  kind: "circle";
  cx: Reactive<number>;
  cy: Reactive<number>;
  r: Reactive<number>;
}

export interface LineShape extends ShapeBase {
  kind: "line";
  x1: Reactive<number>;
  y1: Reactive<number>;
  x2: Reactive<number>;
  y2: Reactive<number>;
  markerEnd?: string;
}

export interface TextShape extends ShapeBase {
  kind: "text";
  x: Reactive<number>;
  y: Reactive<number>;
  text: Reactive<string>;
  fontSize?: number;
  anchor?: "start" | "middle" | "end";
}

export interface PathShape extends ShapeBase {
  kind: "path";
  d: Reactive<string>;
}

export type Shape = RectShape | CircleShape | LineShape | TextShape | PathShape;

export interface EquipmentTemplate {
  type: string;
  /** 本器材几何是否可能整帧重画（含任一 Reactive 字段）——供 svg-renderer 判断增量策略。 */
  geometry: Shape[];
}

const MATERIAL_FILL: Record<string, string> = {
  glass: "#bfe3f0",
  wood: "#c99a5b",
  towel: "#e8b4c8",
  cloth: "#d8cdae",
};

/**
 * 材质中文名唯一真相源——之前 `scene-manager.ts` 自己也建了一份同样内容的表（材质选择按钮文案），
 * 两处各写一份容易漂移，这里统一后 `scene-manager.ts` 改为从这里 import。
 */
export const SURFACE_LABELS: Record<string, string> = {
  glass: "玻璃",
  wood: "木板",
  towel: "毛巾",
  cloth: "棉布",
};

/** 木块挂钩圆环的本地坐标（随朝向变化）——block 几何模板与 scene-manager 的拉绳连线共用同一份
 * 计算，保证绳子端点永远精确落在挂钩圆心上，不会两处数字各写一份而漂移。 */
export function blockHookLocal(orientation: string): { x: number; y: number } {
  return orientation === "vertical" ? { x: 34, y: -35 } : { x: 64, y: -20 };
}

/** 木块：`state.orientation`（flat/vertical）切换宽高比，脚底始终贴合 y=0（局部坐标）。 */
const BLOCK: EquipmentTemplate = {
  type: "block",
  geometry: [
    {
      kind: "rect",
      x: 0,
      y: (s) => (s.orientation === "vertical" ? -70 : -40),
      width: (s) => (s.orientation === "vertical" ? 34 : 64),
      height: (s) => (s.orientation === "vertical" ? 70 : 40),
      rx: 4,
      // 木纹渐变替代纯色矩形（todo/063 P1-09，拟物质感规范①）
      fill: "url(#pe-grad-wood)",
      stroke: "#6b4423",
      strokeWidth: 2,
    },
    {
      // 顶部高光：一条贴着顶边的浅色细线，暗示木块受光的立体感
      kind: "line",
      x1: (s) => (s.orientation === "vertical" ? 4 : 6),
      y1: (s) => (s.orientation === "vertical" ? -68 : -38),
      x2: (s) => (s.orientation === "vertical" ? 30 : 58),
      y2: (s) => (s.orientation === "vertical" ? -68 : -38),
      stroke: "#f2cf9c",
      strokeWidth: 2,
      opacity: 0.6,
    },
    {
      // 挂钩圆环：绳子/拉环的锚点
      kind: "circle",
      cx: (s) => blockHookLocal(String(s.orientation ?? "flat")).x,
      cy: (s) => blockHookLocal(String(s.orientation ?? "flat")).y,
      r: 5,
      fill: "none",
      stroke: "#4b4b4b",
      strokeWidth: 2,
    },
    {
      // 器材标注：真机反馈指出色块完全没有说明，分不清木块/木板/测力计
      kind: "text",
      x: (s) => (s.orientation === "vertical" ? 17 : 32),
      y: (s) => (s.orientation === "vertical" ? -74 : -44),
      text: "木块",
      fontSize: 11,
      anchor: "middle",
      fill: "#3f2a12",
    },
  ],
};

/** 接触面板：`state.material` 决定颜色（与真实纹理无关，用色块区分即可）。 */
const BOARD: EquipmentTemplate = {
  type: "board",
  geometry: [
    {
      kind: "rect",
      x: 0,
      y: 0,
      width: (s) => Number(s.length ?? 480),
      height: 16,
      fill: (s) => MATERIAL_FILL[String(s.material ?? "wood")] ?? MATERIAL_FILL.wood,
      stroke: "#5a4632",
      strokeWidth: 1,
    },
    {
      kind: "text",
      x: (s) => Number(s.length ?? 480) / 2,
      y: 30,
      text: (s) => {
        const material = String(s.material ?? "wood");
        return `接触面 · ${SURFACE_LABELS[material] ?? material}`;
      },
      fontSize: 11,
      anchor: "middle",
      fill: "#4b5563",
    },
  ],
};

/** 单个砝码：`state.stackIndex` 决定局部坐标系里的堆叠层（renderer 用 pose 把整摞定位到木块上方）。 */
const WEIGHT: EquipmentTemplate = {
  type: "weight",
  geometry: [
    {
      kind: "rect",
      x: 0,
      y: (s) => -(Number(s.stackIndex ?? 0) + 1) * 14,
      width: 26,
      height: 12,
      rx: 2,
      // 金属渐变替代纯色矩形（todo/063 P1-09，与 SPRING_SCALE 外壳共用同一份渐变定义）
      fill: "url(#pe-grad-metal)",
      stroke: "#454c58",
      strokeWidth: 1.5,
    },
    {
      // 只在最上面那个砝码标注，堆叠多个时避免文字互相糊住
      kind: "text",
      x: 32,
      y: (s) => -(Number(s.stackIndex ?? 0) + 1) * 14 - 2,
      text: "砝码",
      fontSize: 10,
      anchor: "start",
      fill: "#374151",
      visible: (s) => Number(s.stackIndex ?? 0) === 0,
    },
  ],
};

/** 测力计拉环的本地坐标——力值越大，拉环沿轨道向外移动（"弹簧被拉伸"的具象反馈，真机反馈
 * "抓手不跟手、感觉卡死"的直接修复）。绳子连线端点计算（scene-manager.ts）复用同一份函数，
 * 保证绳头永远精确焊在拉环圆心上。原点(0,0) = 表盘指针轴心 = 底座顶边中点，拉环挂在底座右侧、
 * 与底座垂直中心（y=9）对齐（todo/063 P1-08 重绘后的坐标系）。 */
export const SCALE_HANDLE_BASE_X = 38;
export const SCALE_HANDLE_TRAVEL_PX = 24;
export const SCALE_HANDLE_Y = 9;
export function scaleHandleLocal(appliedForce: number, forceMax: number): { x: number; y: number } {
  const ratio = forceMax > 0 ? Math.min(1, Math.max(0, appliedForce / forceMax)) : 0;
  return { x: SCALE_HANDLE_BASE_X + ratio * SCALE_HANDLE_TRAVEL_PX, y: SCALE_HANDLE_Y };
}

/** 表针旋转角度（度）：-60°（0N，偏左）到 +60°（满量程，偏右），0° = 正上方——替代旧版
 * "手算三角函数端点坐标"，改用 `rotate` 声明式字段做整体旋转（真机反馈指针视觉抖动感强，
 * 根因是端点坐标法在小角度变化时像素取整跳动更明显）。 */
function needleRotationDeg(state: Record<string, unknown>): number {
  const force = Number(state.appliedForce ?? 0);
  const max = Number(state.forceMax ?? 5);
  const ratio = max > 0 ? Math.min(1, Math.max(0, force / max)) : 0;
  return -60 + ratio * 120;
}

/** 表盘弧形扇面路径：以指针轴心(0,0)为圆心，半径 32，从 -60° 扫到 +60°（与表针同一角度基准），
 * 围成一个"披萨扇形"当作表盘底色，比单纯一条弧线更像真实仪表的表盘。 */
const DIAL_WEDGE_PATH = "M 0 0 L -27.7 -16 A 32 32 0 0 1 27.7 -16 Z";

/** 弹簧被拉伸的"弹簧圈"连线：从底座右边缘到拉环之间画一段之字形，力值越大两端距离越远，
 * 视觉上暗示弹簧被拉长——替代原来"拉环凭空悬空在外壳右边"的空隙感。 */
function springCoilPath(fromX: number, toX: number, y: number): string {
  const coils = 4;
  const amplitude = 5;
  const span = toX - fromX;
  const parts = [`M ${fromX.toFixed(1)} ${y}`];
  for (let i = 1; i <= coils; i++) {
    const x = fromX + (span * i) / (coils + 1);
    const dy = i % 2 === 1 ? y - amplitude : y + amplitude;
    parts.push(`L ${x.toFixed(1)} ${dy.toFixed(1)}`);
  }
  parts.push(`L ${toX.toFixed(1)} ${y}`);
  return parts.join(" ");
}

/** 弹簧测力计：仪表化重绘（todo/063 P1-08）——底座 + 半圆弧表盘 + 刻度 + `rotate` 表针 +
 * 弹簧圈连线 + 拉环，整体比例从「68×32 灰色矩形」放大为更接近真实弹簧测力计的仪表造型。
 * 拉环仍是拖拽热点：不再靠"数组第 3 个元素"这种写死下标的脆弱约定标记，而是在 shape 上
 * 声明 `handle: "force"`，svg-renderer 直接按声明打 `data-handle` 属性，新增/调整图形顺序
 * 不会再有错位风险。 */
const SPRING_SCALE: EquipmentTemplate = {
  type: "spring-scale",
  geometry: [
    {
      // 底座：金属渐变，扁平椭圆感的小基座，指针轴心落在它的顶边中点
      kind: "rect",
      x: -30,
      y: 0,
      width: 60,
      height: 18,
      rx: 6,
      fill: "url(#pe-grad-metal)",
      stroke: "#5b6472",
      strokeWidth: 2,
    },
    {
      // 表盘扇面：米白仿旧纸感底色，撑起"这是一个仪表"的第一眼识别
      kind: "path",
      d: DIAL_WEDGE_PATH,
      fill: "var(--pe-dial-face, #fbf7ee)",
      stroke: "#8a94a3",
      strokeWidth: 1.5,
    },
    // 5 条刻度短线：-60°/-30°/0°/30°/60°，与表针角度基准一致
    { kind: "line", x1: -27.7, y1: -16.0, x2: -22.5, y2: -13.0, stroke: "#5b6472", strokeWidth: 1.5 },
    { kind: "line", x1: -16.0, y1: -27.7, x2: -13.0, y2: -22.5, stroke: "#5b6472", strokeWidth: 1.5 },
    { kind: "line", x1: 0, y1: -32.0, x2: 0, y2: -26.0, stroke: "#5b6472", strokeWidth: 1.5 },
    { kind: "line", x1: 16.0, y1: -27.7, x2: 13.0, y2: -22.5, stroke: "#5b6472", strokeWidth: 1.5 },
    { kind: "line", x1: 27.7, y1: -16.0, x2: 22.5, y2: -13.0, stroke: "#5b6472", strokeWidth: 1.5 },
    {
      // 左端刻度数字：0
      kind: "text",
      x: -16.5,
      y: -7,
      text: "0",
      fontSize: 9,
      anchor: "middle",
      fill: "#374151",
    },
    {
      // 右端刻度数字：量程上限（随内容包 forceMax 变化，不写死具体学科的数值）
      kind: "text",
      x: 16.5,
      y: -7,
      text: (s) => `${Math.round(Number(s.forceMax ?? 26))}`,
      fontSize: 9,
      anchor: "middle",
      fill: "#374151",
    },
    {
      // 表针：轴心固定在原点，`rotate` 整体旋转（不再手算端点三角函数）
      kind: "line",
      x1: 0,
      y1: 0,
      x2: 0,
      y2: -26,
      stroke: "#dc2626",
      strokeWidth: 2,
      rotate: (s) => needleRotationDeg(s),
    },
    {
      // 弹簧圈连线：底座右边缘(30, SCALE_HANDLE_Y) 到拉环之间，随力值拉长
      kind: "path",
      d: (s) => {
        const handle = scaleHandleLocal(Number(s.appliedForce ?? 0), Number(s.forceMax ?? 26));
        return springCoilPath(30, handle.x - 14, SCALE_HANDLE_Y);
      },
      fill: "none",
      stroke: "#7d8798",
      strokeWidth: 2,
    },
    {
      // 拉环：拖拽热点，声明 handle:"force"。半径 14——真机反馈"无法拖拽"，原始命中区在真实
      // 屏幕上偏小，加大命中区更容易抓住。cx 随力值移动（"弹簧被拉伸"具象反馈，修"抓手不跟手"）；
      // fill 到量程上限变警示色（修"没有边界提示"）。
      kind: "circle",
      handle: "force",
      cx: (s) => scaleHandleLocal(Number(s.appliedForce ?? 0), Number(s.forceMax ?? 26)).x,
      cy: (s) => scaleHandleLocal(Number(s.appliedForce ?? 0), Number(s.forceMax ?? 26)).y,
      r: 14,
      // 三档变色而不是二值切换：黄（正常）→ 橙（接近上限，≥85%）→ 红（撞到量程上限），给用户在真正
      // 撞线之前就能看到的预警，不是"眼看拉到头，突然才变色"（修真机反馈"边界变现形式没有表现"）。
      fill: (s) => {
        const force = Number(s.appliedForce ?? 0);
        const max = Number(s.forceMax ?? 26);
        if (force >= max) return "#fca5a5";
        if (force >= max * 0.85) return "#fb923c";
        return "#fde68a";
      },
      stroke: "#92400e",
      strokeWidth: 2,
    },
    {
      // 数字读数：仪表下方的大号常驻读数，呼应表盘上的小字刻度
      kind: "text",
      x: 0,
      y: 34,
      text: (s) => `${Number(s.appliedForce ?? 0).toFixed(1)} N`,
      fontSize: 12,
      anchor: "middle",
      fill: "#111827",
    },
    {
      kind: "text",
      x: 0,
      y: -38,
      text: "测力计",
      fontSize: 11,
      anchor: "middle",
      fill: "#374151",
    },
    {
      kind: "text",
      x: 50,
      y: -2,
      text: "拖动施力",
      fontSize: 9,
      anchor: "middle",
      fill: "#92400e",
      // 一旦开始拖就隐藏，避免和移动中的拉环重叠糊在一起
      visible: (s) => Number(s.appliedForce ?? 0) === 0,
    },
    {
      // 边界轨道：常驻可见的短线，标出拉环能移动的范围——拉环从起点滑到轨道尽头 = 视觉上"到边界了"。
      kind: "line",
      x1: SCALE_HANDLE_BASE_X,
      y1: SCALE_HANDLE_Y,
      x2: SCALE_HANDLE_BASE_X + SCALE_HANDLE_TRAVEL_PX,
      y2: SCALE_HANDLE_Y,
      stroke: "#c2c8d1",
      strokeWidth: 3,
      opacity: 0.6,
    },
  ],
};

/** 蜡烛（小孔成像的"物"）：可拖拽（`handle:"distance"`）。局部原点(0,0) = 与孔同高的光轴基准线，
 * **烛焰纵向居中于该基准线**——小孔成像成的像来自烛焰自身发光，不是不发光的蜡烛烛身，
 * 所以上下两个端点（`candleTopLocal`/`candleBottomLocal`）必须卡在烛焰的顶尖/根部，
 * 供 scene-manager 画"光线经过小孔"的示意线；蜡烛烛身+烛座画在烛焰下方，只是承托烛焰的
 * 道具，不参与成像几何（修正：此前烛焰只是烛身顶部的装饰图案，光线锚点错误地卡在烛身
 * 边缘，等于在给"不发光的蜡烛"画像）。 */
const CANDLE_FLAME_SPAN = 46;
const CANDLE_FLAME_BASE_HALF_WIDTH = 8;
const CANDLE_WICK_GAP = 4;
const CANDLE_BODY_WIDTH = 14;
const CANDLE_BODY_HEIGHT = 30;
const CANDLE_BODY_TOP = CANDLE_FLAME_SPAN / 2 + CANDLE_WICK_GAP;

/**
 * 烛焰轮廓：顶端收尖、底部近平（贴合烛芯）的 teardrop——真实火焰形状本就上下不对称，
 * 不是"两端都尖"的对称水滴。`flipped=true` 时整体上下翻转（尖端朝下），供光屏画
 * "倒立的烛焰的像"复用同一个形状函数——像的外形必须和物体（烛焰）呼应，不能是随便的
 * 色块矩形，翻转之后才看得出"倒立"到底倒立在哪。
 */
export function flamePathD(halfSpan: number, baseHalfWidth: number, flipped: boolean): string {
  const sign = flipped ? -1 : 1;
  const tipY = sign * -halfSpan;
  const baseY = sign * halfSpan;
  const nearBaseCtrlY = sign * halfSpan * 0.22;
  const nearTipCtrlY = sign * halfSpan * -0.65;
  const tipHalfWidth = baseHalfWidth * 0.7;
  return `M ${-baseHalfWidth} ${baseY} C ${-baseHalfWidth} ${nearBaseCtrlY} ${-tipHalfWidth} ${nearTipCtrlY} 0 ${tipY} C ${tipHalfWidth} ${nearTipCtrlY} ${baseHalfWidth} ${nearBaseCtrlY} ${baseHalfWidth} ${baseY} Z`;
}

const CANDLE: EquipmentTemplate = {
  type: "candle",
  geometry: [
    {
      // 烛焰——顶尖 = candleTopLocal，底部（贴烛芯处）= candleBottomLocal，是整个器材里
      // 唯一"自身发光、构成小孔成像之物"的部分。
      kind: "path",
      d: flamePathD(CANDLE_FLAME_SPAN / 2, CANDLE_FLAME_BASE_HALF_WIDTH, false),
      // 径向渐变替代纯色块（todo/063 P1-09 拟物质感规范①），核心亮黄→边缘深橙红，
      // 呼应真实烛焰"芯亮边暗"的层次感。
      fill: "url(#pe-grad-flame)",
      stroke: "#c2410c",
      strokeWidth: 1,
    },
    {
      // 烛芯——连接烛焰根部与蜡烛烛身，纯装饰，不参与成像几何。
      kind: "line",
      x1: 0,
      y1: CANDLE_FLAME_SPAN / 2,
      x2: 0,
      y2: CANDLE_BODY_TOP,
      stroke: "#3f2a12",
      strokeWidth: 1.5,
    },
    {
      // 蜡烛烛身——拖拽热点挂在这里（用户直觉上"抓着蜡烛底座移动"），但不发光，
      // 不是 candleTopLocal/candleBottomLocal 的锚点来源。
      kind: "rect",
      handle: "distance",
      x: -CANDLE_BODY_WIDTH / 2,
      y: CANDLE_BODY_TOP,
      width: CANDLE_BODY_WIDTH,
      height: CANDLE_BODY_HEIGHT,
      rx: 2,
      fill: "url(#pe-grad-wood)",
      stroke: "#6b4423",
      strokeWidth: 1.5,
    },
    {
      kind: "text",
      x: 0,
      y: CANDLE_BODY_TOP + CANDLE_BODY_HEIGHT + 16,
      text: "蜡烛",
      fontSize: 11,
      anchor: "middle",
      fill: "#3f2a12",
    },
  ],
};

/** 烛焰顶尖——小孔成像里"物"的上边界，光线示意图据此起笔（不是烛身边缘）。 */
export function candleTopLocal(): { x: number; y: number } {
  return { x: 0, y: -CANDLE_FLAME_SPAN / 2 };
}
/** 烛焰根部（与烛芯相接处）——小孔成像里"物"的下边界。 */
export function candleBottomLocal(): { x: number; y: number } {
  return { x: 0, y: CANDLE_FLAME_SPAN / 2 };
}

/** 点光源（影子实验的光源）：可拖拽（`handle:"distance"`）。局部原点(0,0) = 与遮光物/光屏同高的
 * 地面基线——光源"贴地"放置，是 `computeShadowHeight` 相似三角形模型的几何前提（光线从地面高度
 * 掠过遮光物顶端继续投向光屏）。 */
const LIGHT_RADIUS = 9;
const POINT_LIGHT: EquipmentTemplate = {
  type: "point-light",
  geometry: [
    {
      kind: "circle",
      handle: "distance",
      cx: 0,
      cy: 0,
      r: LIGHT_RADIUS,
      fill: "#fde68a",
      stroke: "#d97706",
      strokeWidth: 2,
    },
    { kind: "line", x1: 0, y1: -LIGHT_RADIUS - 3, x2: 0, y2: -LIGHT_RADIUS - 9, stroke: "#f59e0b", strokeWidth: 2 },
    { kind: "line", x1: 0, y1: LIGHT_RADIUS + 3, x2: 0, y2: LIGHT_RADIUS + 9, stroke: "#f59e0b", strokeWidth: 2 },
    { kind: "line", x1: -LIGHT_RADIUS - 3, y1: 0, x2: -LIGHT_RADIUS - 9, y2: 0, stroke: "#f59e0b", strokeWidth: 2 },
    { kind: "line", x1: LIGHT_RADIUS + 3, y1: 0, x2: LIGHT_RADIUS + 9, y2: 0, stroke: "#f59e0b", strokeWidth: 2 },
    {
      kind: "text",
      x: 0,
      y: LIGHT_RADIUS + 24,
      text: "光源",
      fontSize: 11,
      anchor: "middle",
      fill: "#92400e",
    },
  ],
};

/** 遮光物（影子实验的"物"）：固定不可拖，两端实验各自把中间物体钉死避免两个距离变量耦合
 * （见 todo/068 方案 §2）。局部原点(0,0) = 地面基线，向上是剪影主体，顶端锚点
 * `opaqueObjectTopLocal` 供 scene-manager 画光线。 */
const OPAQUE_OBJECT_HEIGHT = 46;
const OPAQUE_OBJECT_WIDTH = 16;
const OPAQUE_OBJECT: EquipmentTemplate = {
  type: "opaque-object",
  geometry: [
    {
      kind: "path",
      d: `M 0 ${-OPAQUE_OBJECT_HEIGHT} L ${OPAQUE_OBJECT_WIDTH / 2} ${-OPAQUE_OBJECT_HEIGHT * 0.45} L ${-OPAQUE_OBJECT_WIDTH / 2} ${-OPAQUE_OBJECT_HEIGHT * 0.45} Z`,
      fill: "#1f2937",
      stroke: "#111827",
      strokeWidth: 1,
    },
    {
      kind: "rect",
      x: -3,
      y: -OPAQUE_OBJECT_HEIGHT * 0.45,
      width: 6,
      height: OPAQUE_OBJECT_HEIGHT * 0.45,
      fill: "#1f2937",
      stroke: "#111827",
      strokeWidth: 1,
    },
    {
      kind: "text",
      x: 0,
      y: 16,
      text: "遮光物",
      fontSize: 11,
      anchor: "middle",
      fill: "#374151",
    },
  ],
};

export function opaqueObjectTopLocal(): { x: number; y: number } {
  return { x: 0, y: -OPAQUE_OBJECT_HEIGHT };
}

/** 有孔的板（小孔成像的"孔"）：固定不可拖。局部原点(0,0) = 孔的圆心，也是烛/屏共用的光轴
 * 基准高度——三者摆在同一条 `pose.y` 上，靠 `pose.x` 沿光轴前后错开。 */
const PINHOLE_BOARD_WIDTH = 10;
const PINHOLE_BOARD_HEIGHT = 120;
const PINHOLE_BOARD: EquipmentTemplate = {
  type: "pinhole-board",
  geometry: [
    {
      kind: "rect",
      x: -PINHOLE_BOARD_WIDTH / 2,
      y: -PINHOLE_BOARD_HEIGHT / 2,
      width: PINHOLE_BOARD_WIDTH,
      height: PINHOLE_BOARD_HEIGHT,
      fill: "#4b5563",
      stroke: "#374151",
      strokeWidth: 1.5,
    },
    {
      kind: "circle",
      cx: 0,
      cy: 0,
      r: 4,
      fill: "var(--pe-bg-stage, #e3d3ac)",
      stroke: "#1f2937",
      strokeWidth: 1,
    },
    {
      kind: "text",
      x: 0,
      y: PINHOLE_BOARD_HEIGHT / 2 + 16,
      text: "有孔的板",
      fontSize: 11,
      anchor: "middle",
      fill: "#374151",
    },
  ],
};

/** 光屏：小孔成像/影子两个子实验共用同一个模板，可拖拽（`handle:"distance"`）。局部原点(0,0)
 * 与蜡烛/孔或光源/遮光物同一条基准线。`state.markHeightPx`/`state.markKind` 驱动屏幕上的像/影
 * 标记：`markKind==="inverted"`（小孔成像）画一个倒立烛焰轮廓（`flamePathD(flipped:true)`），
 * 以原点为中心对称展开（倒立像跨过光轴两侧）——像的外形要和物体（烛焰）呼应，不是任意色块；
 * `markKind==="upright"`（影子）保持简单深色矩形，从原点（地面基线）向上展开。 */
const SCREEN_HALF_HEIGHT = 100;
const SCREEN_WIDTH = 14;
const MARK_HALF_WIDTH = 6;
const SCREEN: EquipmentTemplate = {
  type: "screen",
  geometry: [
    {
      kind: "rect",
      handle: "distance",
      x: -SCREEN_WIDTH / 2,
      y: -SCREEN_HALF_HEIGHT,
      width: SCREEN_WIDTH,
      height: SCREEN_HALF_HEIGHT * 2,
      rx: 2,
      fill: "#f5efe0",
      stroke: "#c9b98d",
      strokeWidth: 1.5,
    },
    {
      kind: "text",
      x: 0,
      y: SCREEN_HALF_HEIGHT + 16,
      text: "光屏",
      fontSize: 11,
      anchor: "middle",
      fill: "#4b5563",
    },
    {
      // 影子标记：遮光物（opaque-object）本身不是烛焰，影子维持简单深色矩形轮廓。
      kind: "rect",
      x: -MARK_HALF_WIDTH,
      y: (s) => -Number(s.markHeightPx ?? 0),
      width: MARK_HALF_WIDTH * 2,
      height: (s) => Number(s.markHeightPx ?? 0),
      fill: "#1f2937",
      opacity: 0.85,
      visible: (s) => s.markKind === "upright" && Number(s.markHeightPx ?? 0) > 0,
    },
    {
      // 小孔成像的像标记：像的外形必须和物体（烛焰）呼应，不能是随便的色块矩形——
      // 复用 `flamePathD(flipped: true)`，画出"尖端朝下"的倒立烛焰轮廓，直观对应
      // "烛焰倒立成像"这条结论。颜色也复用同一份 `pe-grad-flame` 渐变（不再是紫色矩形）——
      // 像本来就是烛焰自身发出的光形成的，颜色应与烛焰一致，只用略低的不透明度表示
      // "这是投影的像，不是真的火焰"。
      kind: "path",
      d: (s) => flamePathD(Number(s.markHeightPx ?? 0) / 2, MARK_HALF_WIDTH, true),
      fill: "url(#pe-grad-flame)",
      stroke: "#c2410c",
      strokeWidth: 1,
      opacity: 0.8,
      visible: (s) => s.markKind === "inverted" && Number(s.markHeightPx ?? 0) > 0,
    },
  ],
};

const TEMPLATES: Record<string, EquipmentTemplate> = {
  block: BLOCK,
  board: BOARD,
  weight: WEIGHT,
  "spring-scale": SPRING_SCALE,
  candle: CANDLE,
  "point-light": POINT_LIGHT,
  "opaque-object": OPAQUE_OBJECT,
  "pinhole-board": PINHOLE_BOARD,
  screen: SCREEN,
};

export function getEquipmentTemplate(type: string): EquipmentTemplate | undefined {
  return TEMPLATES[type];
}
